import { useEffect, useState } from "react";
import { getPaymentConfig, connectStripe, disconnectStripe, type PaymentConfig, getTerminalConfig, createTerminalLocation, registerReader, getDepositPolicy, setDepositPolicy, getTaxConfig, setTaxConfig, type TaxConfig } from "../../lib/api.ts";

function CardReaderSection({ slug }: { slug: string }) {
  const [locationId, setLocationId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("Front desk");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { getTerminalConfig(slug).then((c) => setLocationId(c.location_id)).catch(() => {}); }, [slug]);

  async function makeLocation() {
    setBusy(true); setErr(null);
    try {
      const { location_id } = await createTerminalLocation(slug, { display_name: displayName, line1: "Front desk", city: "City", country: "US" });
      setLocationId(location_id); setMsg("Location created. Now register your reader.");
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  async function doRegister() {
    setBusy(true); setErr(null); setMsg(null);
    try { await registerReader(slug, { registration_code: code.trim() }); setCode(""); setMsg("Reader registered ✓"); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 4 }}>Card reader (in-person)</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>
        Use an internet-connected Stripe reader (e.g. WisePOS E) to take card payments at the desk.
      </p>
      {err && <div className="alert-error" style={{ marginBottom: 8 }}>{err}</div>}
      {msg && <div style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 8 }}>{msg}</div>}
      {!locationId ? (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13 }}>Location name<input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={{ marginTop: 4 }} /></label>
          <button type="button" className="btn-secondary" disabled={busy} onClick={makeLocation}>Create location</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ fontSize: 13 }}>Reader registration code<input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. quick-brown-fox" style={{ marginTop: 4 }} /></label>
          <button type="button" className="btn-secondary" disabled={busy || !code.trim()} onClick={doRegister}>Register reader</button>
        </div>
      )}
    </div>
  );
}

function DepositPolicySection({ slug }: { slug: string }) {
  const [enabled, setEnabled] = useState(false);
  const [kind, setKind] = useState<"percent" | "fixed">("percent");
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { getDepositPolicy(slug).then((p) => { setEnabled(p.deposit_enabled); setKind(p.deposit_kind); setValue(p.deposit_value); }).catch(() => {}); }, [slug]);
  async function save() {
    setSaving(true); setMsg(null);
    try { await setDepositPolicy(slug, { deposit_enabled: enabled, deposit_kind: kind, deposit_value: value }); setMsg("Saved ✓"); }
    catch { setMsg("Could not save"); } finally { setSaving(false); }
  }
  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 4 }}>Deposit at booking</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>Require a deposit on your public booking page before a booking is confirmed.</p>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 10 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Require a deposit
      </label>
      {enabled && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
          <label style={{ fontSize: 13 }}>Type
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as "percent" | "fixed")} style={{ marginTop: 4 }}>
              <option value="percent">Percent of total</option>
              <option value="fixed">Fixed amount (cents)</option>
            </select>
          </label>
          <label style={{ fontSize: 13 }}>{kind === "percent" ? "Percent (1-100)" : "Amount (cents)"}
            <input className="input" type="number" value={value} onChange={(e) => setValue(parseInt(e.target.value, 10) || 0)} style={{ marginTop: 4, width: 140 }} />
          </label>
        </div>
      )}
      <button type="button" className="btn-secondary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save deposit policy"}</button>
      {msg && <span style={{ marginLeft: 10, fontSize: 13, color: "var(--text-muted)" }}>{msg}</span>}
    </div>
  );
}

function TaxSection({ slug }: { slug: string }) {
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [ratePct, setRatePct] = useState("0");
  const [label, setLabel] = useState("Tax");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    getTaxConfig(slug).then((t: TaxConfig) => {
      setTaxEnabled(t.tax_enabled);
      setRatePct(String(t.tax_rate_bps / 100));
      setLabel(t.tax_label ?? "Tax");
    }).catch(() => {});
  }, [slug]);

  async function save() {
    setSaving(true); setMsg(null);
    try {
      const bps = Math.round(parseFloat(ratePct) * 100) || 0;
      await setTaxConfig(slug, { tax_enabled: taxEnabled, tax_rate_bps: bps, tax_label: label || null });
      setMsg("Saved ✓");
    } catch { setMsg("Could not save"); } finally { setSaving(false); }
  }

  return (
    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
      <h3 style={{ fontSize: "1rem", fontWeight: 600, marginBottom: 4 }}>Tax</h3>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>Automatically calculate and display tax on bills and checkout.</p>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 10 }}>
        <input type="checkbox" checked={taxEnabled} onChange={(e) => setTaxEnabled(e.target.checked)} /> Enable tax
      </label>
      {taxEnabled && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 10 }}>
          <label style={{ fontSize: 13 }}>Rate (%)
            <input className="input" type="number" min="0" max="100" step="0.01" value={ratePct} onChange={(e) => setRatePct(e.target.value)} style={{ marginTop: 4, width: 120 }} />
          </label>
          <label style={{ fontSize: 13 }}>Label
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. GST" style={{ marginTop: 4, width: 160 }} />
          </label>
        </div>
      )}
      <button type="button" className="btn-secondary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save tax settings"}</button>
      {msg && <span style={{ marginLeft: 10, fontSize: 13, color: "var(--text-muted)" }}>{msg}</span>}
    </div>
  );
}

export function PaymentsTab({ slug }: { slug: string }) {
  const [cfg, setCfg] = useState<PaymentConfig | null>(null);
  const [key, setKey] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    getPaymentConfig(slug).then((c) => { setCfg(c); setCurrency(c.default_currency); }).catch((e: Error) => setError(e.message));
  }
  useEffect(() => { load(); }, [slug]);

  async function onConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await connectStripe(slug, { secret_key: key.trim(), default_currency: currency });
      setKey(""); load();
    } catch (err) {
      const e = err as { error?: { message?: string }; message?: string };
      setError(e.error?.message ?? e.message ?? "Could not connect Stripe");
    } finally { setSaving(false); }
  }

  async function onDisconnect() {
    setSaving(true); setError(null);
    try { await disconnectStripe(slug); load(); }
    catch (err) { setError((err as Error).message); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: 4 }}>Payments</h2>
      <p style={{ color: "var(--text-muted)", fontSize: 14, marginBottom: 16 }}>
        Connect your own Stripe account to collect payments from your customers. Money goes straight to your Stripe — zedule takes no fee.
      </p>
      {error && <div className="alert-error" style={{ marginBottom: 12 }}>{error}</div>}
      {cfg?.connected ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="badge-green">Connected ✓</span>
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>Default currency: {cfg.default_currency.toUpperCase()}</span>
            <button type="button" className="btn-secondary" disabled={saving} onClick={onDisconnect} style={{ marginLeft: "auto" }}>
              {saving ? "…" : "Disconnect"}
            </button>
          </div>
          <CardReaderSection slug={slug} />
          <DepositPolicySection slug={slug} />
        </div>
      ) : (
        <form onSubmit={onConnect} style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 480 }}>
          <label style={{ fontSize: 13, fontWeight: 500 }}>
            Stripe secret or restricted key
            <input className="input" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk_live_… or rk_live_…" autoComplete="off" style={{ marginTop: 4 }} />
          </label>
          <label style={{ fontSize: 13, fontWeight: 500 }}>
            Default currency
            <input className="input" value={currency} onChange={(e) => setCurrency(e.target.value.toLowerCase())} placeholder="usd" style={{ marginTop: 4, maxWidth: 120 }} />
          </label>
          <button type="submit" className="btn-primary" disabled={saving || !key.trim()} style={{ width: "fit-content" }}>
            {saving ? "Connecting…" : "Connect Stripe"}
          </button>
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            We never store card data — payments use Stripe's hosted checkout. Use a restricted key with Checkout, PaymentIntents, Refunds, Webhook Endpoints, and Terminal permissions.
          </p>
        </form>
      )}

      {/* Tax applies to cash bills too — available without a Stripe connection. */}
      <TaxSection slug={slug} />
    </div>
  );
}
